<template>
    <div class="add-episode-share-tab">
        <embed-section
            title="Embed player"
            :script="embedScript"
        >
            <template #after-title>
                <a
                    href="#"
                    class="title-help"
                    target="_blank"
                >(how to install)</a>
            </template>
        </embed-section>

        <p>
            Embed your form with one line of JavaScript. Place this code where you want the form to display on your page.
        </p>

        <router-link
            class="advanced-settings-link"
            :to="`/shows/${show.hash}/?edit=true&type=${playerType}&player=${trackNo}&showEmbed=true`"
        >
            Show advanced embed settings
        </router-link>

        <div class="share-section">
            <div class="h4">
                Share Episode
            </div>

            <div class="share-buttons">
                <button
                    class="btn share-button share-button-copy"
                    @click="copyUrl"
                >
                    <i class="far fa-link share-button-icon" />
                    Copy URL
                </button>
                <a
                    v-for="(social, key) in socials"
                    :key="key"
                    :href="social.url"
                    :class="`btn share-button share-button-${key}`"
                    @click.prevent="shareEpisode"
                >
                    <i :class="`fab fa-${social.icon || key} share-button-icon`" />
                    {{ social.label }}
                </a>
                <a
                    class="btn share-button share-button-email"
                    :href="emailUrl"
                >
                    <i class="far fa-envelope share-button-icon" />
                    Email
                </a>
            </div>
        </div>
    </div>
</template>
<script>
// External dependencies.
import { mapState } from 'vuex';
// Internal dependencies.
import EmbedSection from '@/components/Embed/EmbedSection';
import toasts from '@/mixins/toasts';
import { getEmbedScriptTag } from '@/utils/embedTags';
import { writeToClipboard } from '@/utils/clipboard';

export default {
    components: {
        EmbedSection,
    },

    mixins: [toasts],

    props: {
        show: {
            type: Object,
            required: true,
        },
    },

    data: () => ({
        /**
         * Determines type of embed player. For single episode use "track".
         *
         * @type {string}
         */
        playerType: 'track',
    }),

    computed: {
        ...mapState({
            seasonNo: state => state.UIState.addEpisodeModal.episode?.seasonNo || 1,
            episodeNo: state => state.UIState.addEpisodeModal.episode?.episodeNo || 0,
            episodeTitle: state => state.UIState.addEpisodeModal.episode?.title || '',
        }),

        /**
         * Return track number.
         *
         * @returns {string} Track number.
         */
        trackNo() {
            if ('serial' === this.show.podcastType) {
                return `${this.seasonNo}:${this.episodeNo}`;
            }

            return this.episodeNo;
        },

        /**
         * Returns URL to Episode Player.
         *
         * @returns {string} URl to Episode Player.
         */
        shareUrl() {
            return `${this.$appUrl}/embed/player/${this.playerType}/${this.show.hash}/${this.trackNo}`;
        },

        /**
         * Returns script to embed.
         *
         * @returns {string} Script HTML tag.
         */
        embedScript() {
            return getEmbedScriptTag(this.playerType, {}, this.show, this.trackNo);
        },

        /**
         * Returns list of all social platforms.
         *
         * @returns {object} List of social platforms.
         */
        socials() {
            return {
                facebook: {
                    label: 'Facebook',
                    icon: 'facebook-square',
                    url: this.facebookUrl,
                },
                twitter: {
                    label: 'Twitter',
                    url: this.twitterUrl,
                },
                linkedin: {
                    label: 'LinkedIn',
                    url: this.linkedInUrl,
                },
            };
        },

        /**
         * Returns URL to share on Facebook.
         *
         * This method is still available but might stop working in the future.
         *
         * @see https://stackoverflow.com/q/20956229/4664371  Legacy implementation (current)
         * @see https://developers.facebook.com/docs/sharing/reference/share-dialog  Modern implementation
         * @returns {string} Share URL.
         */
        facebookUrl() {
            return `https://www.facebook.com/sharer/sharer.php?u=${this.shareUrl}`;
        },

        /**
         * Returns URL to share on Twitter.
         *
         * @see https://developer.twitter.com/en/docs/twitter-for-websites/tweet-button/overview
         * @returns {string} Share URL.
         */
        twitterUrl() {
            return `https://twitter.com/intent/tweet?url=${this.shareUrl}&text=${this.episodeTitle}`;
        },

        /**
         * Returns URL to share on LinkedIn.
         *
         * This method is still available but might stop working in the future.
         *
         * @see https://stackoverflow.com/a/10737122/4664371  Legacy implementation (current)
         * @see https://docs.microsoft.com/en-us/linkedin/consumer/integrations/self-serve/share-on-linkedin  Modern implementation
         * @returns {string} Share URL.
         */
        linkedInUrl() {
            return `http://www.linkedin.com/shareArticle?mini=true&url=${this.shareUrl}`;
        },

        /**
         * Returns URL to share by email.
         *
         * @returns {string} URL to share by email.
         */
        emailUrl() {
            return `mailto:?subject=${this.episodeTitle}&body=${this.shareUrl}`;
        },
    },

    methods: {
        /**
         * Copies episode URL to clipboard.
         */
        async copyUrl() {
            await writeToClipboard(this.shareUrl);

            this.toastSuccess({ message: { title: 'URL copied to clipboard.' } });
        },

        /**
         * Opens new window for sharing Episode.
         *
         * @param  {Event} event Click event.
         */
        shareEpisode(event) {
            const url = event.target.getAttribute('href');

            window.open(url, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes');
        },
    },
};
</script>
